  function myPumpHub(config) {
    return {
      dataUrl: config.dataUrl,

      categories: [],
      pumps: [],
      activeFilter: 'all',
      showBackToTop: false,
      searchQuery: '',
      modelHelpOpen: false,
      docsModal: { open: false, pump: null, type: null },
      loading: true,
      error: false,

      async init() {
        try {
          const res = await fetch(this.dataUrl);
          if (!res.ok) throw new Error('Failed to fetch pump data');
          const data = await res.json();
          this.categories = data.categories || [];
          this.pumps = data.pumps || [];
        } catch (e) {
          console.error('My Pump hub: could not load data', e);
          this.error = true;
        } finally {
          this.loading = false;
        }
      },

      // Strips spaces, hyphens, slashes, and pipes so "EZ8" matches "EZ-8",
      // "V25 012" matches "V25-012", etc. Keeps letters/numbers only.
      normalize(str) {
        return str.toLowerCase().replace(/[\s\-\/|]/g, '');
      },

      // Appends a Shopify CDN width parameter so the browser fetches a
      // resized image instead of the full-resolution source (these are
      // typically 2400x2400 originals, but cards only ever display
      // around 300px). Safe no-op for anything that isn't a real
      // cdn.shopify.com URL yet.
      resizeImage(url, width) {
        if (!url || !url.startsWith('http')) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}width=${width}`;
      },

      // Looks up the display label for a pump's category from the
      // categories array already in the JSON (e.g. "12v-24v" becomes "12V & 24V Pumps").
      categoryLabel(categoryId) {
        const match = this.categories.find((c) => c.id === categoryId);
        return match ? match.label : categoryId;
      },

      hasLinkType(pump, type) {
        return pump.links.some((l) => l.type === type);
      },

      collectionProducts: [],
      collectionProductsLoading: false,
      collectionProductsError: false,

      openDocs(pump, type) {
        this.docsModal = { open: true, pump, type };
        this.collectionProducts = [];
        this.collectionProductsError = false;

        if (type === 'parts') {
          const partsLink = pump.links.find(
            (l) => l.type === 'parts' && l.label === 'Parts & Accessories'
          );
          if (partsLink) {
            this.fetchCollectionProducts(partsLink.url);
          }
        }
      },

      // Pulls up to 4 live products from the pump's actual Parts &
      // Accessories collection, using Shopify's public /products.json
      // endpoint (no API key needed — same data any storefront page can
      // already see). Deliberately reads the "Parts & Accessories"
      // labeled link specifically, NOT "Visit Product Collection" —
      // that one points at the pump's own product/series page (used by
      // the footer link) and is a different collection entirely.
      async fetchCollectionProducts(collectionUrl) {
        this.collectionProductsLoading = true;
        try {
          const separator = collectionUrl.includes('?') ? '&' : '?';
          const res = await fetch(`${collectionUrl}/products.json${separator}limit=4`);
          if (!res.ok) throw new Error('Failed to fetch collection products');
          const data = await res.json();
          this.collectionProducts = (data.products || []).map((p) => ({
            title: p.title,
            url: `/products/${p.handle}`,
            image: p.images && p.images[0] ? p.images[0].src : null,
            price: p.variants && p.variants[0] && p.variants[0].price
              ? '$' + parseFloat(p.variants[0].price).toFixed(2)
              : null,
          }));
        } catch (e) {
          console.error('My Pump hub: could not load collection preview', e);
          this.collectionProductsError = true;
        } finally {
          this.collectionProductsLoading = false;
        }
      },

      docTypeLabel(type) {
        return { manual: 'Manuals & Documents', video: 'Videos', parts: 'Parts & Accessories' }[type] || '';
      },

      get activeDocsTitle() {
        if (!this.docsModal.pump) return '';
        return this.docsModal.pump.model + ' ' + this.docTypeLabel(this.docsModal.type);
      },

      get activeDocsList() {
        if (!this.docsModal.pump) return [];
        return this.docsModal.pump.links.filter(
          (l) => l.type === this.docsModal.type && !/visit product/i.test(l.label)
        );
      },

      get filteredPumps() {
        let result =
          this.activeFilter === 'all'
            ? this.pumps
            : this.pumps.filter((p) => p.category === this.activeFilter);

        const q = this.searchQuery.trim().toLowerCase();
        if (q) {
          const qNormalized = this.normalize(q);
          result = result.filter((p) => {
            const haystack = [p.model, p.subtitle, ...(p.keywords || [])].join(' ');
            // Match either the raw lowercase text (handles spec/word searches
            // like "20 gpm") or the punctuation-stripped version (handles
            // model-number variations like "EZ8" finding "EZ-8").
            return (
              haystack.toLowerCase().includes(q) ||
              this.normalize(haystack).includes(qNormalized)
            );
          });
        }

        return result;
      },
    };
  }
